import {Store} from 'vuex'
import {ListenerRequestMessage, ListenerType, LoginRequest, MessageAction} from '@/socket/socketInterfaces';

const doneStatuses = new Set<number>([1000, 1001]);

export default class TaskWebsocket {
  private socket: WebSocket;
  private retryCount = 0;
  private isAuthed = false;
  private store: Store<any>;

  constructor(store: Store<any>) {
    this.store = store;
    this.socket = this.buildSocket();
  }

  close() {
    this.socket.close(1000);
  }

  public fetchForUser(userId: number) {
    if (this.isAuthed && this.socket.readyState === 1){
      this.send(new ListenerRequestMessage(MessageAction.ADD, ListenerType.Listener_UserTasks, { userId: userId }));
    }
  }

  public updateIndividualIds(individualIds: number[]) {
    if (this.isAuthed && this.socket.readyState === 1) {
      const action = individualIds.length > 0 ? MessageAction.REMOVE_ADD : MessageAction.REMOVE;
      this.send(new ListenerRequestMessage(action, ListenerType.Listener_TaskIndividual, {taskIds: individualIds}));
    }
  }

  public watchDelegated(watchDelegated: boolean) {
    if (this.isAuthed && this.socket.readyState === 1) {
      const action = watchDelegated ? MessageAction.REMOVE_ADD : MessageAction.REMOVE;
      this.send(new ListenerRequestMessage(action, ListenerType.Listener_DelegatedTasks, { userId: this.store.state.user.appUserId }));
    }
  }

  private buildSocket(): WebSocket {
    const socket = new WebSocket(process.env.WS_BASE_URL);
    socket.onopen = () => this.login();
    socket.onclose = event => this.handleClose(event);
    socket.onerror = () => {};
    socket.onmessage = async event => {
      try {
        await this.handleMessage(JSON.parse(event.data));
      } catch (err) {
        console.error(err);
      }
    };
    return socket;
  }

  private login() {
    const token = this.store.state.user.token;
    if (!token) {
      this.socket.close();
      return;
    }
    this.send(new LoginRequest(token));
  }

  private send(data: LoginRequest | ListenerRequestMessage) {
    this.socket.send(JSON.stringify(data));
  }

  private handleClose(event: any) {
    this.socket.onopen = null as any;
    this.socket.onclose = null as any;
    this.socket.onerror = null as any;
    this.socket.onmessage = null as any;
    if (event.code === 1008 || event.reason === 'Unauthorized') {
      return;
    } else if (doneStatuses.has(event.code)) {
      return;
    }
    if (this.retryCount < 3) {
      this.retryCount += 1;
      this.socket = this.buildSocket();
    }
  }

  private async handleMessage(message: any) {
    if (message.authComplete === true) {
      this.retryCount = 0;
      this.isAuthed = true;
      this.register();
    } else {
      switch (message.type) {
        case ListenerType.Listener_UserTasks:
          this.store.commit('task/setTasks', {tasks: message.data.tasks, userId: message.data.userId});
          // Once we have the tasks we'll rely on individual listeners
          this.send(new ListenerRequestMessage(MessageAction.REMOVE, ListenerType.Listener_UserTasks));
          break;
        case ListenerType.Listener_TaskAdded:
          await this.store.dispatch('task/addTask', message.data.task);
          break;
        case ListenerType.Listener_TaskUpdated:
          await this.store.dispatch('task/updateTask', message.data.task);
          break;
        case ListenerType.Listener_TaskDeleted:
          await this.store.dispatch('task/deleteTask', message.data.id);
          break;
        case ListenerType.Listener_TaskChecklistTemplate:
          this.store.commit('task/setTemplates', message.data.templates);
          break;
        case ListenerType.Listener_TaskIndividual:
          this.store.commit('task/individualTaskUpdated', message.data.task);
          break;
        case ListenerType.Listener_DelegatedTasks:
          this.store.commit('task/setDelegatedTasks', message.data.tasks);
          break;
        case ListenerType.Listener_TaskColumnReordered:
          await this.store.dispatch('task/reorderTasks', message.data.taskReorderData);
          break;
        default:
          console.log('Received message from the server ' + JSON.stringify(message));
      }
    }
  }

  private register() {
    if (this.isAuthed && this.socket.readyState === 1) {
      this.send(new ListenerRequestMessage(MessageAction.ADD, ListenerType.Listener_UserTasks, { userId: this.store.state.user.appUserId }));
      if (this.store.state.task.userIdInFocus && this.store.state.user.appUserId !== this.store.state.task.userIdInFocus) {
        this.send(new ListenerRequestMessage(MessageAction.ADD, ListenerType.Listener_UserTasks, { userId: this.store.state.task.userIdInFocus }));
      }
      this.send(new ListenerRequestMessage(MessageAction.ADD, ListenerType.Listener_TaskUpdated));
      this.send(new ListenerRequestMessage(MessageAction.ADD, ListenerType.Listener_TaskAdded));
      this.send(new ListenerRequestMessage(MessageAction.ADD, ListenerType.Listener_TaskDeleted));
      this.send(new ListenerRequestMessage(MessageAction.ADD, ListenerType.Listener_TaskChecklistTemplate, { userId: this.store.state.user.appUserId }));
      this.send(new ListenerRequestMessage(MessageAction.ADD, ListenerType.Listener_TaskColumnReordered));
      this.updateIndividualIds(this.store.state.task.individualTaskIds);
      this.watchDelegated(this.store.state.task.watchDelegated);
    }
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/socket/TaskWebsocket.ts